import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import LockResetIcon from "@mui/icons-material/LockReset";
import PersonOffOutlinedIcon from "@mui/icons-material/PersonOffOutlined";
import HowToRegOutlinedIcon from "@mui/icons-material/HowToRegOutlined";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import {
  ApplicationRole,
  ManagedUserResponse,
  UsersService,
} from "../../../api";
import { TabsHelpers } from "../../../components/basic/TabsHelpers";
import { isUserActive, toggleUserActive } from "./userActiveActions";
import ResetPasswordDialog from "./ResetPasswordDialog";

const MySwal = withReactContent(Swal);
const COLUMN_COUNT = 9;
const DEFAULT_ROWS_PER_PAGE = 25;

type Order = "asc" | "desc";
type SortKey =
  | "index"
  | "name"
  | "email"
  | "phone"
  | "roles"
  | "status"
  | "joined"
  | "updated";

type HeadCell = {
  id: SortKey;
  label: string;
  width?: number | string;
};

const HEAD_CELLS: HeadCell[] = [
  { id: "index", label: "#", width: 56 },
  { id: "name", label: "Name" },
  { id: "email", label: "Email address" },
  { id: "phone", label: "Phone" },
  { id: "roles", label: "Roles" },
  { id: "status", label: "Status" },
  { id: "joined", label: "Joined" },
  { id: "updated", label: "Last updated" },
];

const ROLE_LABELS: Record<string, string> = {
  [ApplicationRole.ADMIN]: "Admin",
  [ApplicationRole.ATTORNEY]: "Attorney",
  [ApplicationRole.STAFF]: "Staff",
  [ApplicationRole.STAFF_ATTORNEY]: "Staff Attorney",
  [ApplicationRole.USER]: "User",
  [ApplicationRole.REFFERAL_STAFF]: "Referral Staff",
  [ApplicationRole.CLIO_STAFF]: "Clio Staff",
  [ApplicationRole.CLIO_ADMIN]: "Clio Admin",
  [ApplicationRole.CLIO_MANAGER]: "Clio Manager",
  [ApplicationRole.CLIO_TASK_ASSIGNER]: "Clio Task Assigner",
  [ApplicationRole.QA]: "QA",
};

const denseCellSx = { height: "auto", py: "6px", px: 1, lineHeight: 1.5 };
const iconBtnSx = { p: 0.35 };
const chipSx = { height: 26, fontSize: "0.85rem", fontWeight: 600 };

function formatRole(role: ApplicationRole | string) {
  return ROLE_LABELS[role] ?? role;
}

function formatDate(value?: string | null) {
  if (!value) return "—";
  const date = dayjs(value);
  return date.isValid() ? date.format("MM/DD/YYYY") : "—";
}

function formatRolesText(roles?: ApplicationRole[] | null) {
  if (!roles?.length) return "";
  return roles.map(formatRole).join(", ");
}

function getSortValue(user: ManagedUserResponse, key: SortKey): string | number {
  switch (key) {
    case "index":
      return user.id;
    case "name":
      return `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim().toLowerCase();
    case "email":
      return (user.email ?? "").toLowerCase();
    case "phone":
      return (user.phoneNumber ?? "").toLowerCase();
    case "roles":
      return formatRolesText(user.roles).toLowerCase();
    case "status": {
      const active = isUserActive(user) ? 1 : 0;
      const pending = user.isPending ? 1 : 0;
      return active * 2 + pending;
    }
    case "joined":
      return user.createdAt ? dayjs(user.createdAt).valueOf() : 0;
    case "updated":
      return user.updatedAt ? dayjs(user.updatedAt).valueOf() : 0;
    default:
      return "";
  }
}

function compareUsers(
  a: ManagedUserResponse,
  b: ManagedUserResponse,
  orderBy: SortKey,
  order: Order
) {
  const aValue = getSortValue(a, orderBy);
  const bValue = getSortValue(b, orderBy);

  let result = 0;
  if (typeof aValue === "number" && typeof bValue === "number") {
    result = aValue - bValue;
  } else {
    result = String(aValue).localeCompare(String(bValue), undefined, {
      sensitivity: "base",
      numeric: true,
    });
  }

  return order === "asc" ? result : -result;
}

function matchesSearch(user: ManagedUserResponse, searchTerm: string) {
  const lower = searchTerm.toLowerCase();
  const fullName = `${user.firstName ?? ""} ${user.lastName ?? ""}`.toLowerCase();
  const rolesText = formatRolesText(user.roles).toLowerCase();

  return (
    fullName.includes(lower) ||
    (user.email?.toLowerCase().includes(lower) ?? false) ||
    (user.phoneNumber?.toLowerCase().includes(lower) ?? false) ||
    rolesText.includes(lower)
  );
}

export default function StaffUsersTable() {
  const [value, setValue] = useState(0);
  const [users, setUsers] = useState<ManagedUserResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState<string>("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [orderBy, setOrderBy] = useState<SortKey>("name");
  const [order, setOrder] = useState<Order>("asc");
  const [resetPasswordUser, setResetPasswordUser] =
    useState<ManagedUserResponse | null>(null);
  const navigate = useNavigate();

  const loadUsers = useCallback(() => {
    setLoading(true);
    UsersService.postApiUsersGetManagedUsers({ includeInactive: true })
      .then(setUsers)
      .catch(() => {
        setUsers([]);
        MySwal.fire({
          icon: "error",
          title: <p>Could not load users.</p>,
          text: "If this is a new deployment, make sure the database migration for user deactivation has been applied.",
        });
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const activeUsers = useMemo(
    () => users.filter((user) => isUserActive(user)),
    [users]
  );
  const inactiveUsers = useMemo(
    () => users.filter((user) => !isUserActive(user)),
    [users]
  );

  const tabUsers = value === 0 ? activeUsers : inactiveUsers;

  const availableRoles = useMemo(() => {
    const roles = new Set<ApplicationRole>();
    users.forEach((user) => user.roles?.forEach((role) => roles.add(role)));
    return Array.from(roles).sort((a, b) =>
      formatRole(a).localeCompare(formatRole(b))
    );
  }, [users]);

  const filteredUsers = useMemo(() => {
    let list = tabUsers;
    if (searchTerm.trim()) {
      list = list.filter((user) => matchesSearch(user, searchTerm.trim()));
    }
    if (roleFilter !== "all") {
      list = list.filter((user) =>
        user.roles?.includes(roleFilter as ApplicationRole)
      );
    }
    return [...list].sort((a, b) => compareUsers(a, b, orderBy, order));
  }, [tabUsers, searchTerm, roleFilter, orderBy, order]);

  const paginatedUsers = useMemo(
    () =>
      filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredUsers, page, rowsPerPage]
  );

  const handleRequestSort = (property: SortKey) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
    setPage(0);
  };

  const handleToggleActive = async (
    user: ManagedUserResponse,
    isActive: boolean
  ) => {
    await toggleUserActive(user.id, {
      firstName: user.firstName,
      lastName: user.lastName,
      isActive,
      onSuccess: loadUsers,
    });
  };

  const handleEdit = (user: ManagedUserResponse) => {
    navigate(`/staff-users/${user.id}`);
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="h5" className="title" fontWeight={600}>
            Users
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Manage accounts, roles, access, and passwords.
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate("/generateLink?setAny=true")}
        >
          Create registration link
        </Button>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
        <Tabs
          value={value}
          onChange={(_, newValue) => {
            setValue(newValue);
            setPage(0);
          }}
        >
          <Tab
            label={`ACTIVE (${activeUsers.length})`}
            {...TabsHelpers.parseProps(0)}
          />
          <Tab
            label={`INACTIVE (${inactiveUsers.length})`}
            {...TabsHelpers.parseProps(1)}
          />
        </Tabs>
      </Box>

      <Paper
        variant="outlined"
        sx={{
          px: 2,
          py: 1.5,
          mb: 2,
          bgcolor: "secondary.main",
          borderColor: "secondary.dark",
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1.5}
          alignItems={{ md: "center" }}
        >
          <TextField
            placeholder="Search by name, email, phone, or role..."
            size="small"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(0);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: "gray.contrastText" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: { xs: "100%", md: 360 },
              bgcolor: "background.paper",
              borderRadius: 1,
            }}
          />
          <FormControl
            size="small"
            sx={{ minWidth: 180, bgcolor: "background.paper", borderRadius: 1 }}
          >
            <InputLabel id="role-filter-label">Role</InputLabel>
            <Select
              labelId="role-filter-label"
              label="Role"
              value={roleFilter}
              onChange={(e) => {
                setRoleFilter(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="all">All roles</MenuItem>
              {availableRoles.map((role) => (
                <MenuItem key={role} value={role}>
                  {formatRole(role)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      <TableContainer>
        <Table
          size="small"
          sx={{
            minWidth: 1100,
            "& .MuiTableCell-root": {
              height: "auto",
              py: "12px",
              px: 1,
              lineHeight: 1.25,
            },
            "& .MuiTableCell-head": {
              py: "12px",
            },
            "& .MuiIconButton-root": {
              p: 0.35,
            },
            "& .MuiSvgIcon-root": {
              fontSize: "1.35rem",
            },
            "& .MuiChip-root": {
              height: 26,
              fontSize: "0.85rem",
            },
            "& .MuiChip-label": {
              px: 1,
            },
          }}
        >
          <TableHead>
            <TableRow>
              {HEAD_CELLS.map((headCell) => (
                <TableCell
                  key={headCell.id}
                  sortDirection={orderBy === headCell.id ? order : false}
                  sx={{
                    ...denseCellSx,
                    width: headCell.width,
                    minWidth: headCell.width,
                  }}
                >
                  <TableSortLabel
                    active={orderBy === headCell.id}
                    direction={orderBy === headCell.id ? order : "asc"}
                    onClick={() => handleRequestSort(headCell.id)}
                  >
                    {headCell.label}
                  </TableSortLabel>
                </TableCell>
              ))}
              <TableCell align="right" sx={{ ...denseCellSx, width: 120 }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} align="center" sx={{ py: 4 }}>
                  Loading users...
                </TableCell>
              </TableRow>
            ) : filteredUsers.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={COLUMN_COUNT}
                  align="center"
                  sx={{ py: 4, color: "text.secondary" }}
                >
                  No users match the selected filters.
                </TableCell>
              </TableRow>
            ) : (
              paginatedUsers.map((user, index) => (
                <TableRow tabIndex={-1} key={user.id} hover>
                  <TableCell sx={denseCellSx}>
                    {page * rowsPerPage + index + 1}
                  </TableCell>
                  <TableCell sx={denseCellSx}>
                    {user.firstName} {user.lastName}
                  </TableCell>
                  <TableCell sx={denseCellSx}>{user.email}</TableCell>
                  <TableCell sx={denseCellSx}>{user.phoneNumber || "—"}</TableCell>
                  <TableCell sx={denseCellSx}>
                    <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                      {(user.roles ?? []).length === 0
                        ? "—"
                        : (user.roles ?? []).map((role) => (
                            <Chip
                              key={role}
                              size="small"
                              label={formatRole(role)}
                              color="secondary"
                              sx={{
                                ...chipSx,
                                color: "secondary.contrastText",
                              }}
                            />
                          ))}
                    </Box>
                  </TableCell>
                  <TableCell sx={denseCellSx}>
                    <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                      {isUserActive(user) ? (
                        <Chip
                          size="small"
                          label="Active"
                          sx={{
                            ...chipSx,
                            bgcolor: "dirtyGreen.main",
                            color: "dirtyGreen.contrastText",
                          }}
                        />
                      ) : (
                        <Chip
                          size="small"
                          label="Inactive"
                          variant="outlined"
                          sx={chipSx}
                        />
                      )}
                      {user.isPending && (
                        <Chip
                          size="small"
                          label="Pending"
                          color="secondary"
                          sx={{
                            ...chipSx,
                            color: "secondary.contrastText",
                          }}
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell sx={denseCellSx}>{formatDate(user.createdAt)}</TableCell>
                  <TableCell sx={denseCellSx}>{formatDate(user.updatedAt)}</TableCell>
                  <TableCell align="right" sx={denseCellSx}>
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.25,
                      }}
                    >
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          color="primary"
                          sx={iconBtnSx}
                          onClick={() => handleEdit(user)}
                          aria-label="Edit user"
                        >
                          <EditOutlinedIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Reset password">
                        <IconButton
                          size="small"
                          color="primary"
                          sx={iconBtnSx}
                          onClick={() => setResetPasswordUser(user)}
                          aria-label="Reset password"
                        >
                          <LockResetIcon />
                        </IconButton>
                      </Tooltip>
                      {isUserActive(user) ? (
                        <Tooltip title="Deactivate">
                          <IconButton
                            size="small"
                            color="error"
                            sx={iconBtnSx}
                            onClick={() => handleToggleActive(user, false)}
                            aria-label="Deactivate user"
                          >
                            <PersonOffOutlinedIcon />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Reactivate">
                          <IconButton
                            size="small"
                            color="primary"
                            sx={iconBtnSx}
                            onClick={() => handleToggleActive(user, true)}
                            aria-label="Reactivate user"
                          >
                            <HowToRegOutlinedIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {!loading && filteredUsers.length > 0 && (
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={filteredUsers.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      )}

      <ResetPasswordDialog
        open={!!resetPasswordUser}
        user={resetPasswordUser}
        onClose={() => setResetPasswordUser(null)}
      />
    </Box>
  );
}
